import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "Servicios", id: "#servicios" },
  { label: "Nosotros", id: "#nosotros" },
  { label: "Casos de éxito", id: "#casos" },
  { label: "Contacto", id: "#contacto" },
];

const TOOLS = [
  { label: "Calculadora de valoración", desc: "Estima el valor de tu empresa en 3 pasos", anchor: "#valoracion" },
  { label: "Calculadora fiscal de la venta", desc: "IRPF vs. sociedad holding", to: "/herramientas/calculadora-fiscal" },
  { label: "Diagnóstico de circulante", desc: "Caja inmovilizada y coste de financiación", to: "/herramientas/diagnostico-circulante" },
  { label: "¿Cuánto puedo pagar por una empresa?", desc: "Tu capacidad de compra prudente", to: "/herramientas/cuanto-puedo-pagar" },
  { label: "Test: preparada para venderse", desc: "Autodiagnóstico de 4 minutos", to: "/herramientas/test-venta" },
  { label: "Simulador de márgenes", desc: "Ventas y costes frente a EBITDA y valor", to: "/herramientas/simulador-margenes" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [tools, setTools] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTools(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 450);
    } else {
      scrollToId(id);
    }
  };

  const goRoute = (to) => {
    setOpen(false);
    setTools(false);
    navigate(to);
  };

  const openTool = (t) => {
    if (t.anchor) go(t.anchor);
    else goRoute(t.to);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "border-b border-gold/10 bg-ink/90 backdrop-blur-md"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 md:px-10">
        <a
          href="#"
          data-testid="nav-logo"
          onClick={(e) => {
            e.preventDefault();
            go("top");
          }}
          className="flex items-center"
        >
          <img src="/logo-light.png" alt="AJG M&A Consulting" className="h-12 w-auto" />
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.slice(0, 3).map((l) => (
            <a
              key={l.id}
              href={l.id}
              data-testid={`nav-link-${l.id.slice(1)}`}
              onClick={(e) => {
                e.preventDefault();
                go(l.id);
              }}
              className="text-[13px] font-medium uppercase tracking-[0.18em] text-slate-200 transition-colors hover:text-gold"
            >
              {l.label}
            </a>
          ))}

          <div className="relative">
            <button
              data-testid="nav-tools-toggle"
              onClick={() => setTools(!tools)}
              className="flex items-center gap-1.5 text-[13px] font-medium uppercase tracking-[0.18em] text-slate-200 transition-colors hover:text-gold"
            >
              Herramientas
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${tools ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {tools && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 top-full mt-3 w-80 border border-gold/20 bg-ink-2 shadow-2xl"
                >
                  {TOOLS.map((t, i) => (
                    <button
                      key={t.label}
                      data-testid={`nav-tool-${i}`}
                      onClick={() => openTool(t)}
                      className="block w-full border-t border-gold/10 px-5 py-3.5 text-left text-sm text-slate-200 transition-colors first:border-t-0 hover:bg-gold/10 hover:text-gold"
                    >
                      <span className="font-semibold">{t.label}</span>
                      <span className="mt-0.5 block text-xs text-slate-500">{t.desc}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <a
            href="#contacto"
            data-testid="nav-link-contacto"
            onClick={(e) => {
              e.preventDefault();
              go("#contacto");
            }}
            className="text-[13px] font-medium uppercase tracking-[0.18em] text-slate-200 transition-colors hover:text-gold"
          >
            Contacto
          </a>
          <a
            href="#contacto"
            data-testid="nav-cta"
            onClick={(e) => {
              e.preventDefault();
              go("#contacto");
            }}
            className="border border-gold/60 px-5 py-2.5 text-[13px] font-semibold uppercase tracking-[0.18em] text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
          >
            Consulta confidencial
          </a>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="p-2 text-gold lg:hidden"
          aria-label="Abrir menú"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-gold/10 bg-ink/95 backdrop-blur-md lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <a
                  key={l.id}
                  href={l.id}
                  data-testid={`nav-mobile-link-${l.id.slice(1)}`}
                  onClick={(e) => {
                    e.preventDefault();
                    go(l.id);
                  }}
                  className="py-3 text-sm font-medium uppercase tracking-[0.18em] text-slate-200 transition-colors hover:text-gold"
                >
                  {l.label}
                </a>
              ))}
              <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-gold/70">Herramientas</p>
              {TOOLS.map((t, i) => (
                <button
                  key={t.label}
                  data-testid={`nav-mobile-tool-${i}`}
                  onClick={() => openTool(t)}
                  className="py-3 text-left text-sm font-medium uppercase tracking-[0.18em] text-slate-200 transition-colors hover:text-gold"
                >
                  {t.label}
                </button>
              ))}
              <a
                href="#contacto"
                data-testid="nav-mobile-cta"
                onClick={(e) => {
                  e.preventDefault();
                  go("#contacto");
                }}
                className="mt-3 mb-2 bg-gold px-5 py-3 text-center text-[13px] font-semibold uppercase tracking-[0.18em] text-ink"
              >
                Consulta confidencial
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
